import {
  buildPath,
  buildTemplatePath,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  Permission,
  Student,
  StudentEditResource,
  StudentEnrollment as StudentEnrollmentModel,
  StudentViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import ApiService from "services/apiService";
import { StudentEnrollment } from "./StudentEnrollment";
import { StudentPageHeader } from "./StudentPageHeader";
import { BasicInfo } from "../StudentPage/StudentProfile/BasicInfo";
import { StudentParents } from "./StudentParents/StudentParents";
import { setAppToast } from "redux/reducers/appSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { studentPageRoute } from "../StudentPage/StudentPage";

const routeName = "createStudent";

export const studentCreatePageRoute: PageRoute = {
  handle: "New Student",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const StudentCreatePage = () => {
  const ROOT_PERMISSION = Permission.Student;
  const [student, setStudent] = useState<Student>({} as Student);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [student] });
  const [viewResource, setViewResource] = useState<StudentViewResource>({});
  const [editResource, setEditResource] = useState<StudentEditResource>({} as StudentEditResource);
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [step, setStep] = useState(1);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);

  useEffect(() => {
    fetch(0);
    fetchViewResource(0);
    fetchEditResource();
  }, []);

  const fetch = (id: number) => {
    ApiService.students.get(id).then((response) => {
      setStudent(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = (id: number) => {
    ApiService.students.getViewResource(id).then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.students.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const save = () => {
    setValidationErrors({});

    ApiService.students
      .saveCreate(student)
      .then((response) => {
        const id = response;
        navigate(`../${studentPageRoute.getPath({ id: id, crudMode: "view" })}`);
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  return (
    <Page
      isForm
      header={
        <StudentPageHeader
          handle={studentCreatePageRoute.handle}
          student={student}
          setValidationErrors={setValidationErrors}
          unsavedChanges={unsavedChanges}
          step={step}
          setStep={setStep}
        />
      }
    >
      {step === 1 && (
        <Stack spacing="lg">
          <BasicInfo
            student={student}
            unsavedChanges={unsavedChanges}
            save={save}
            setStudent={setStudent}
            viewResource={viewResource}
            editResource={editResource}
            crudMode={"edit"}
            validationErrors={validationErrors}
          />

          <StudentEnrollment
            enrollment={student.creatingFirstEnrollment || ({} as StudentEnrollmentModel)}
            setEnrollment={(enrollment) =>
              setStudent(
                produce((draft) => {
                  draft.creatingFirstEnrollment = enrollment;
                })
              )
            }
            validationErrors={validationErrors[pn<Student>("creatingFirstEnrollment")] || {}}
          />
        </Stack>
      )}

      {step === 2 && (
        <StudentParents
          studentParents={student.creatingStudentParents || []}
          setStudentParents={(studentParents) =>
            setStudent(
              produce((draft) => {
                draft.creatingStudentParents = studentParents;
              })
            )
          }
          validationErrors={validationErrors[pn<Student>("creatingStudentParents")] || []}
          resetValidationErrors={() => setValidationErrors({})}
        />
      )}
    </Page>
  );
};
